'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { FUN_GAMES, type FunGame } from './funGames';
import { CREDIT_DISCLAIMER, CREDIT_GO_PLAY, CREDIT_HUMAN, CREDIT_LEAD, NEAL_URL } from './nealFunCredit';
import { useLocalBest } from './useLocalBest';

/**
 * The arcade, wrapped so its provenance is structural rather than a footnote.
 *
 * The credit used to sit in its own block *below* the game grid, which read as
 * unrelated small print — you could scan the whole grid without ever learning
 * where the ideas came from. Here the attribution is the section's header, and
 * the games are visibly inside that container, so "these came from neal.fun" is
 * the first thing you read and cannot be missed.
 */

const GameCard = t0 => {
  const $ = _c(17);
  const {
    game
  } = t0;
  const best = useLocalBest(game.bestKey ?? "");
  let t1;
  if ($[0] !== best || $[1] !== game) {
    t1 = best !== null && game.formatBest ? game.formatBest(best) : null;
    $[0] = best;
    $[1] = game;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const bestLabel = t1;
  const t2 = `/games/${game.slug}`;
  let t3;
  if ($[3] !== game.emoji) {
    t3 = <span aria-hidden="true" className="text-3xl">{game.emoji}</span>;
    $[3] = game.emoji;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== game.title) {
    t4 = <h3 className="mt-2 font-emphasis text-textPrimary">{game.title}</h3>;
    $[5] = game.title;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== game.tagline) {
    t5 = <p className="mt-1 flex-1 text-sm leading-snug text-textSecondary">{game.tagline}</p>;
    $[7] = game.tagline;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = bestLabel ?? "";
  let t7;
  if ($[9] !== t6) {
    t7 = <p className="mt-3 min-h-4 text-xs font-emphasis uppercase tracking-wide text-textGray">{t6}</p>;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== t7) {
    t8 = <Link href={t2} className="flex flex-col rounded-xl border border-ds-border-standard bg-background-primary p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-ds-border-hover hover:bg-background-light">{t3}{t4}{t5}{t7}</Link>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
};
const NealFunArcade = () => {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p className="text-xs font-emphasis uppercase tracking-wide text-textGray">The arcade</p>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h2 id="arcade-heading" className="mt-1 text-xl font-bold tracking-heading-2 text-textPrimary sm:text-2xl">Inspired by{" "}<a href={NEAL_URL} target="_blank" rel="noopener noreferrer" className="underline decoration-primary decoration-2 underline-offset-4 transition-colors hover:text-primary">neal.fun</a></h2>;
    t2 = <p className="mt-2 max-w-2xl text-sm leading-relaxed text-textSecondary">{CREDIT_LEAD} {CREDIT_HUMAN}</p>;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <header className="border-b border-ds-border-subtle pb-5">{t0}{t1}{t2}<div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2"><a href={NEAL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-emphasis text-white transition-colors hover:bg-indigo-400">Play the originals at neal.fun<span aria-hidden="true">↗</span></a><span className="text-sm text-textSecondary">{CREDIT_GO_PLAY}</span></div></header>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{FUN_GAMES.map(_temp)}</div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <section aria-labelledby="arcade-heading" className="rounded-2xl border border-ds-border-standard bg-background-secondary p-5 sm:p-6">{t3}{t4}<footer className="mt-5 border-t border-ds-border-subtle pt-4"><p className="text-xs leading-relaxed text-textGray">{CREDIT_DISCLAIMER} No account needed to play — your best scores are kept on this device, and follow you across devices once you sign in.</p></footer></section>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
};
export default NealFunArcade;
function _temp(game) {
  return <GameCard key={game.slug} game={game} />;
}